<script lang="ts" setup>
import './tile.scss';
import type { Performer, Service } from '@/ontology/performer';
import config from '@/config';
import { storeToRefs } from 'pinia';
import { computed, defineAsyncComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { usePerformerStore } from '@/stores/performer';
import { useUserStore } from '@/stores/user';
import { useModalsStore } from '@/stores/modals';
import { useLocalizationStore } from '@/stores/localization';
import { onBeforeRouteUpdate, onBeforeRouteLeave, useRoute } from 'vue-router';
import { performerStatusColor } from '@/api/performer/utils';
import { getSliderVideos, getAvatar, videoUrl } from '../../utils';
import { useCamStore } from '@/stores/cam';
import { useMobileTeaserStore } from '@/stores/mobileTeaser';
import { getStaticImages } from '@/utils';
import { debounce } from 'lodash-es';
import Spinner from '@/components/grid/spinner/spinner.vue';

const favorite = defineAsyncComponent(() => import('@/components/buttons/favorite/favorite.vue'));
const tileContent = defineAsyncComponent(() => import('@/components/grid/tile-content.vue'));

const isImageLoaded = ref(false);
const userStore = useUserStore();
const { language, marketing, activeCampagain, hasIVR, TileServices } = storeToRefs(useLocalizationStore());
const { safeMode } = storeToRefs(useUserStore());
const { isLoggedIn } = useUserStore();
const { toggleModal } = useModalsStore();
const route = useRoute();

const videoPlayer = ref<HTMLVideoElement | null>(null);
const avatar = ref<HTMLImageElement | null>(null);

const showAvatar = ref(true);
const showPoster = ref(false);
const isPlaying = ref(false);
const isLoaded = ref(false);
const isLoading = ref(false);
const isAutoplayBlocked = ref(false);
const timer = ref<NodeJS.Timer | undefined>(undefined);
const tileElement = ref<HTMLElement | null>(null);
const tileContentIndexes = [2, 7, 18, 23, 31];
const theme = ref(import.meta.env.VITE_Themename);

let observer: IntersectionObserver | null = null;
let imageObserver: IntersectionObserver | null = null;

const props = withDefaults(
    defineProps<{ id: number, colums: string, link: string, index?: number, page?: number, type?: string, showAdvert?:boolean, service?: string, current?: number }>(),
    { service: 'profile'}
);

const performerStore = usePerformerStore();
const mobileStore = useMobileTeaserStore();
const performer = computed(() => performerStore.getById(props.id));
const isMobile = computed(() => userStore.isMobile());

onMounted(() => {
    init();
});

function init(){
    if ('IntersectionObserver' in window) {
        initVideoObserver();
        lazyLoadImages();
    } else {
        // support for older browser
        if(avatar.value) {
            avatar.value.src = avatar.value.dataset.src!;
        }
    }
}

onBeforeUnmount(() => {
    if (observer && tileElement.value) {
        observer.unobserve(tileElement.value);
    }
    if (imageObserver && avatar.value) {
        imageObserver.unobserve(avatar.value);
    }
});

function lazyLoadImages() {
    imageObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if(entry.isIntersecting && avatar.value) {
                    avatar.value.src = avatar.value.dataset.src!;
                    imageObserver?.unobserve(avatar.value);
                }
            })
        }, { rootMargin: '100px'})

    if (avatar.value) {
        imageObserver.observe(avatar.value);
    }
}

function initVideoObserver() {
    setTimeout(() => {
        observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (hasVideo()) {
                    if (entry.isIntersecting) {
                        mobileStore.addPlayable(props.id);
                    } else {
                        mobileStore.removePlayable(props.id);
                    }
                }
            })
        }, { threshold: 0.8})

        if (tileElement.value) {
            observer.observe(tileElement.value);
        }
    }, 2000);
}

watch(() => mobileStore.currentPlay, (newValue, oldValue) => {
    if (newValue === props.id) {
        if (!isPlaying.value && !isAutoplayBlocked.value) {
            playVideo();
        }
    } else {
        if (isPlaying.value) {
            stopVideo();
        }
    }
})

watch(() => userStore.isSafeMode, (newValue, oldValue) => {
    if (newValue != oldValue) {
        init();
    }
})

function addSource() {
    const videoPlayerElement: HTMLVideoElement | undefined = getVideoPlayer();
    if (videoPlayerElement) {
        if (!videoPlayerElement.firstChild) {
            const source = document.createElement('source');
            source.type = 'video/mp4';
            source.src = videoUrl(performer.value.id, performer.value!.avatarMedia!);
            videoPlayerElement.appendChild(source);
        }
    }
}

function getVideoPlayer(): HTMLVideoElement | undefined {
    return hasVideo() && videoPlayer.value ? videoPlayer.value as HTMLVideoElement : undefined;
}

function onImageLoad() {
    isImageLoaded.value = true;
}

function stopTimer() {
    if (timer.value) {        
        clearTimeout(timer.value);
        timer.value = undefined;        
    }
}

function startTimer() {
    timer.value = setTimeout(() => {  
        const videoPlayerElement: HTMLVideoElement | undefined = getVideoPlayer();
        if (videoPlayerElement && isPlaying) {
            requestAnimationFrame(() => {
                videoPlayerElement.pause();
            });            
        }           
    }, 450);
}

function onPause() {
    const videoPlayerElement: HTMLVideoElement | undefined = getVideoPlayer();
    if (videoPlayerElement && isLoaded.value) {     
        stopTimer();    
        showPoster.value = false;
        isPlaying.value = false;   
        isLoading.value = false;           
        videoPlayerElement.onloadeddata = null;
        videoPlayerElement.onloadstart = null;
        videoPlayerElement.load();
    }
}

function hasVideo() {
    if (performer.value) {
        return performer.value.avatarMedia !== undefined;
    }
}

function addEventHandlers(video: HTMLVideoElement) {
    if (!video.oncanplay) {
        video.oncanplay = (ev) => {  isLoading.value = false;   }
    }
   
    if (!video.onloadeddata) {   
               
        video.onloadeddata = () => {
            isLoaded.value = true;
            isLoading.value = false;
            startPlaying();
        }

        video.load();
    }

    if (!video.onloadstart) {
        video.onloadstart = () => {
            isLoading.value = true;
        }
    }

}

function playVideo(mouse = false) {
    if (hasVideo()) { 
        if (mouse) {
            if (mobileStore.current === props.id) {
                return;
            } else {      
                mobileStore.stopTimer(true);
            }          
        } 

        const videoPlayerElement: HTMLVideoElement | undefined = getVideoPlayer();
        if (videoPlayerElement) {   
            isPlaying.value = true;
            addEventHandlers(videoPlayerElement);
            addSource();   
            stopTimer();
                                                                         
            if (isLoaded.value) {
                startPlaying();
            }
        } 
    }
}

function startPlaying() {
    const videoPlayerElement: HTMLVideoElement | undefined = getVideoPlayer();
    if (videoPlayerElement) {
        showPoster.value = false;
        showAvatar.value = false;
        requestAnimationFrame(()=>{
            videoPlayerElement.play()
                .then(() => {
                    isPlaying.value = true;
                }).catch((error) => {
                    if(error.name === "NotAllowedError") {
                        console.error('Autoplay was prevented:', error);
                        isAutoplayBlocked.value = true;
                    } else {
                        console.error('Error playing video:', error);
                    }
                    isPlaying.value=false;
                });
        })
    }      
}

function stopVideo(mouse = false) {    
    const videoPlayerElement: HTMLVideoElement | undefined = getVideoPlayer();
    if (videoPlayerElement) {
        if (mouse && mobileStore.current === props.id) {
            return;
        } 
        stopTimer();
        if (!isLoaded.value) { 
            videoPlayerElement.onloadeddata = null; 
            isLoaded.value = false;
            isLoading.value = false;
        } else {
            showPoster.value = true;
        }

        startTimer();
    }    
}

const isCurrent = computed( ()=> {
    return props.current ? Number(props.current) === performer.value.advertNumber : false;
});

const availableServices = computed( ()=>
    TileServices.value.filter( service => {
        if (service == "toy"){
            return performer.value.services[service].status != 'off';
        } else {
            return performer.value.services[service].status == 'available';
        }
    })
);

const shouldShowTileContent = computed(() => {
     if (theme.value !== 'dusk') return false;
     return props.index && tileContentIndexes.indexOf(props.index) !== -1 && props.page === 1 && !isMobile.value;
})

const marketingBanner = computed( ()=> {
    const active = activeCampagain.value.status && props.index === 2 && route.name === 'grid' && config.Themename === 'thuis';
    let img = "";

    switch (language?.value) {
        case "nl":
            img = `/themes/${config.Themename}/assets/images/banners/${language?.value}/${activeCampagain.value.name}.png`;
            return { active: active, img: img };
        case "en":
            img = `/themes/${config.Themename}/assets/images/banners/${language?.value}/${activeCampagain.value.name}.png`;
            return { active: active , img: img };
        case "de":
            img = `/themes/${config.Themename}/assets/images/banners/${language?.value}/${activeCampagain.value.name}.png`;
            return { active: active, img: img };
        default:
            return { active: false, img: img };
    }
})

const promoBanner = computed( ()=> {
    const page = activeCampagain.value.status ? false : true;
    const active = props.index === 38 && page && route.name === 'grid';
    const typeLink = hasIVR.value || isLoggedIn() ? 'promo' : 'register';
    let img = `/themes/${config.Themename}/assets/images/banners/${language?.value}/${typeLink === 'promo' || isLoggedIn() ? 'discountBanner' : 'registerBanner'}.png`; 
    return { active: active, img: img, type: typeLink }
});

const mobileBanner = computed( ()=> {
    const active = navigator.userAgent.match(/Android/i) 
    || navigator.userAgent.match(/Mobile/i)
    || navigator.userAgent.match(/webOS/i)
    || navigator.userAgent.match(/iPhone/i)
    || navigator.userAgent.match(/IEMobile/i)
    || navigator.userAgent.match(/BlackBerry/i)
    || navigator.userAgent.match(/Windows Phone/i);
    const show = active && window.screen.width < 768 && (props.index === 1 && props.page === 1) && config.Sitename === 'Thuis.nl';
    let img = `/themes/${config.Themename}/assets/images/banners/${language?.value}/mobileApp.png`;
    return { active: show, img: img, href: `https://app.thuis.nl/install/?u=${userStore.account.id}&utm_src=mobile-banner` }
});

function tileRoute(){    
    switch( props.link ){
        case 'profile':
            return { name: 'profile', params: { advert:performer.value.advertNumber } };
        case 'peek':
            return { name: 'videochat', params: { advert:performer.value.advertNumber, service: 'peek' } };
        default:
            throw `${props.link} is no link I ever heard of for a tile`
    }
}

const debouncedPlayVideo = debounce(() => playVideo(true), 250);
const debouncedStopVideo = debounce(() => stopVideo(true), 250);

const loaderStyle = computed(() => ({
  'background-image': `url(${getStaticImages('loading', 'png')})`
}));

const hideLabel = computed(() => {
    if (performer.value) {
        return performer.value.status === 'OFFLINE';
    }
    return false
});
</script>

<template src="./tile.html"></template>